import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  Page,
  PageRef,
  PopularOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
} from '../types.ts';
import type { ProxyFetchOpts } from '../../lib/proxy.ts';
import { ParseError } from '../../lib/errors.ts';
import { createGameBusApi, type GameBusApiOptions } from './api.ts';
import { buildGameBusSearchFilters, parseGameBusChapters, parseGameBusComic, parseGameBusPageList, parseGameBusPages, parseGameBusTags } from './parsers.ts';
import {
  GAMEBUS_SORT_OPTIONS,
  GAMEBUS_SOURCE_ID,
  type GameBusTagOption,
} from './types.ts';

let tagCatalogPromise: Promise<GameBusTagOption[]> | undefined;

function createGameBusSource(options: GameBusApiOptions = {}): ComicSource {
  const api = createGameBusApi(options);

  async function tags() {
    if (options.proxyFetch) return api.listTags();
    tagCatalogPromise ??= api.listTags();
    return tagCatalogPromise;
  }

  return {
    id: GAMEBUS_SOURCE_ID,
    name: 'GameBus',
    description: 'No login required. Public GameBus JSON API with direct CDN images.',
    capabilities: {
      login: false,
      favorites: true,
      multiFavCategories: false,
      search: true,
      advancedSearch: false,
      sortOptions: GAMEBUS_SORT_OPTIONS.map((option) => option.value),
      loginNotes:
        'No login required. Browse, search, filter by GameBus tags, and read anonymously. Your favorites are stored locally on this device.',
    },

    async getSearchFilters(_auth?: SourceAuth): Promise<SearchFiltersSchema> {
      return buildGameBusSearchFilters(await tags());
    },

    async search(query: SearchQuery, _auth?: SourceAuth): Promise<Page<ComicCard>> {
      return api.listComics(query);
    },

    async getPopular(opts?: PopularOpts, _auth?: SourceAuth): Promise<Page<ComicCard>> {
      return api.listComics({ page: opts?.page, cursor: opts?.cursor });
    },

    async getComic(id: string, _auth?: SourceAuth): Promise<ComicDetails> {
      return api.getComic(id);
    },

    async getPages(
      id: string,
      _auth?: SourceAuth,
      opts?: { readUnitId?: string },
    ): Promise<PageRef[]> {
      return api.getPages(id, opts?.readUnitId);
    },

    async resolveImageUrl(ref: PageRef, _auth?: SourceAuth): Promise<{ url: string }> {
      if (ref.resolvedUrl) return { url: ref.resolvedUrl };
      const fallback = ref.extra?.resolvedUrl;
      if (typeof fallback === 'string' && fallback.trim()) return { url: fallback };
      throw new ParseError('GameBus page reference did not include a resolved image URL', {
        source: GAMEBUS_SOURCE_ID,
      });
    },

    async validateAuth(_auth: SourceAuth): Promise<{ ok: boolean }> {
      return { ok: true };
    },
  };
}

const source = {
  ...createGameBusSource(),
  createForTest: (options: { proxyFetch: (url: string, opts?: ProxyFetchOpts) => Promise<Response> }) =>
    createGameBusSource(options),
} satisfies ComicSource & {
  createForTest: (options: { proxyFetch: (url: string, opts?: ProxyFetchOpts) => Promise<Response> }) => ComicSource;
};

export {
  buildGameBusSearchFilters,
  createGameBusSource,
  parseGameBusChapters,
  parseGameBusComic,
  parseGameBusPageList,
  parseGameBusPages,
  parseGameBusTags,
};
export default source;
